/**
 * Scan-rule vocabulary for the Review scans editor (TRA-1062): the labels
 * the selects show, the rule shape the editor holds, and cap validation.
 */

import {
  DreamScanRuleField,
  DreamScanRuleMatch,
  DreamScanRuleMode,
  type DreamScanRule,
  type DreamScanSettings,
  type DreamScanSettingsSelection,
  type UpdateDreamScanRequest,
} from '@/api/generated/model'

import type { RunDay } from './runDays'
import { limitsBody, limitsFromSettings, limitsKey, type TeamLimitsDraft } from './teamLimits'

/**
 * A rule as the editor holds it. The API fills `mode` and `match` on every
 * rule it returns (a stored row written before TRA-1062 reads as
 * exclude/exact), so the editor never sees them missing; the generated
 * type marks them optional only because the request schema defaults them.
 */
export type ScanRule = Required<DreamScanRule>

export function toScanRule(rule: DreamScanRule): ScanRule {
  return {
    mode: rule.mode ?? DreamScanRuleMode.exclude,
    field: rule.field,
    match: rule.match ?? DreamScanRuleMatch.exact,
    value: rule.value,
  }
}

export function newScanRule(): ScanRule {
  return { mode: 'exclude', field: 'client_tenant_id', match: 'exact', value: '' }
}

export const MODE_LABELS: Record<DreamScanRuleMode, string> = {
  exclude: 'Exclude',
  include: 'Include only',
}

export const MATCH_LABELS: Record<DreamScanRuleMatch, string> = {
  exact: 'equals',
  regex: 'matches regex',
}

export const FIELD_LABELS: Record<DreamScanRuleField, string> = {
  client_tenant_id: 'Tenant (X-Tenant-Id)',
  environment: 'Environment',
  traffic_type: 'Traffic type',
  session_id: 'Session',
  initiated_by: 'Initiated by',
}

export const MODE_OPTIONS: readonly DreamScanRuleMode[] = Object.values(DreamScanRuleMode)
export const MATCH_OPTIONS: readonly DreamScanRuleMatch[] = Object.values(DreamScanRuleMatch)
export const FIELD_OPTIONS: readonly DreamScanRuleField[] = Object.values(DreamScanRuleField)

export const CAP_MAX = 1000

export function validateCap(capText: string): string | null {
  if (capText.trim() === '') return null
  const value = Number(capText)
  if (!Number.isInteger(value) || value < 0 || value > CAP_MAX) {
    return `The cap must be a whole number between 0 and ${String(CAP_MAX)}.`
  }
  return null
}

/** Rules as a save sends them: values trimmed. */
export function trimRules(rules: readonly ScanRule[]): ScanRule[] {
  return rules.map((rule) => ({ ...rule, value: rule.value.trim() }))
}

/**
 * One canonical string per set of rules, so a forecast computed from a
 * draft can be matched against the rules on screen. Trimmed like a save,
 * so a stray space never hides a forecast that was computed for it.
 */
export function rulesKey(rules: readonly ScanRule[]): string {
  return JSON.stringify(trimRules(rules))
}

/**
 * The form's three inputs as one value, so "is anything unsaved" is one
 * comparison. The cap is compared as the number a save would send (so
 * "050" reads as 50) and rule values trimmed: what a save would drop is
 * not a change worth flagging.
 */
export interface DreamScanDraft {
  capText: string
  rules: ScanRule[]
  runDays: RunDay[]
  /** How the judge's runs are picked (TRA-1511); staff only on the page. */
  selection: DreamScanSettingsSelection
  /** The second review's per-team limits (TRA-1569 patch 4); staff only on the page. */
  limits: TeamLimitsDraft
}

export function draftFromSettings(settings: DreamScanSettings): DreamScanDraft {
  return {
    capText: settings.dream_scan_default === null ? '' : String(settings.dream_scan_default),
    rules: settings.rules.map(toScanRule),
    runDays: settings.run_days,
    selection: settings.selection,
    limits: limitsFromSettings(settings),
  }
}

/** The cap as a save sends it: null for empty, else the parsed number. */
export function capValue(capText: string): number | null {
  return capText.trim() === '' ? null : Number(capText)
}

/**
 * The draft as a save sends it. The second review limits go only on a
 * staff save (`includeLimits`): the API keeps a customer's stored values
 * either way, and leaving them out keeps the two readings the same.
 */
export function saveBody(draft: DreamScanDraft, includeLimits: boolean): UpdateDreamScanRequest {
  return {
    dream_scan_default: capValue(draft.capText),
    rules: trimRules(draft.rules),
    run_days: draft.runDays,
    selection: draft.selection,
    ...(includeLimits ? limitsBody(draft.limits) : {}),
  }
}

export function isDraftDirty(saved: DreamScanDraft, current: DreamScanDraft): boolean {
  return (
    capValue(saved.capText) !== capValue(current.capText) ||
    rulesKey(saved.rules) !== rulesKey(current.rules) ||
    saved.runDays.join(',') !== current.runDays.join(',') ||
    saved.selection !== current.selection ||
    limitsKey(saved.limits) !== limitsKey(current.limits)
  )
}
